import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { AuthCheckResponse, AuthenticatedUser } from '../domain/interfaces/auth-sign-in-response';
import {
  authCheck,
  authCheckSilent,
  authRequestAuthCode,
  authRestore,
  authSignIn,
  authSignOut,
  authSignUp,
  clearActiveTournamentId,
  switchUserTournament,
} from './actions';
import { loadState } from './persist';

type AuthSliceState = {
  loading: boolean;
  error: { [key: string]: string } | null;
  authed: boolean;
  authChecking: boolean;
  authChecked: boolean;
  user: AuthenticatedUser | null;
  activeTournamentId: number | null;
  hasTournaments: boolean;
  codeRequesting: boolean;
  codeRequested: boolean;
  signInError: string | null;
};

const persistedState = loadState();

const initialState: AuthSliceState = {
  loading: false,
  error: null,
  authed: false,
  authChecking: false,
  authChecked: false,
  user: null,
  hasTournaments: false,
  codeRequesting: false,
  codeRequested: false,
  signInError: null,
  ...persistedState,
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthChecking(state) {
      state.codeRequested = false;
      state.codeRequesting = false;
    },
    setTournamentCreated(state, action: PayloadAction<number>) {
      state.hasTournaments = true;
      if (!state.activeTournamentId) {
        state.activeTournamentId = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authRestore.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(authRestore.fulfilled, (state) => {
      state.loading = false;
    });

    builder.addCase(authRestore.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(authRequestAuthCode.pending, (state) => {
      state.codeRequesting = true;
    });

    builder.addCase(authRequestAuthCode.fulfilled, (state) => {
      state.codeRequesting = false;
      state.codeRequested = true;
    });

    builder.addCase(authRequestAuthCode.rejected, (state) => {
      state.codeRequesting = false;
      state.codeRequested = false;
    });

    builder.addCase(authSignIn.pending, (state) => {
      state.loading = true;
      state.signInError = null;
    });

    builder.addCase(authSignIn.fulfilled, (state) => {
      state.loading = false;
      state.authed = true;
      state.codeRequested = false;
      state.signInError = null;
    });

    builder.addCase(authSignIn.rejected, (state, action) => {
      state.loading = false;
      state.authed = false;
      state.signInError = action.error.message ?? null;
    });

    builder.addCase(authSignUp.pending, (state) => {
      state.codeRequesting = true;
      state.error = null;
    });

    builder.addCase(authSignUp.fulfilled, (state) => {
      state.codeRequesting = false;
      state.codeRequested = true;
      state.error = null;
    });

    builder.addCase(authSignUp.rejected, (state, action) => {
      state.codeRequesting = false;
      state.codeRequested = false;

      const fieldErrors = action.payload?.errors;
      if (fieldErrors) {
        state.error = Object.keys(fieldErrors).reduce<{ [key: string]: string }>((result, key) => {
          result[key] = fieldErrors[key][0];
          return result;
        }, {});
      } else {
        state.error = null;
      }
    });

    builder.addCase(authCheck.fulfilled, (state, action) => {
      applyAuthCheckResult(state, action.payload);
    });

    builder.addCase(authCheckSilent.fulfilled, (state, action) => {
      // В отличие от `authCheck` (он с пустым ответом уводит на вход), «входа нет» здесь — ровно отказ: иначе
      // `authed: true` без пользователя пустил бы через `SecureRoute`, когда окно расширят до компьютера.
      if (action.payload?.user) {
        applyAuthCheckResult(state, action.payload);
      } else {
        rejectAuthCheck(state);
      }
    });

    builder.addCase(authSignOut, (state) => {
      state.authed = false;
      state.user = null;
      state.hasTournaments = false;
      state.activeTournamentId = null;
    });

    builder.addCase(switchUserTournament, (state, action) => {
      state.activeTournamentId = action.payload;
    });

    builder.addCase(clearActiveTournamentId, (state) => {
      state.activeTournamentId = null;
    });

    // Тихая проверка с заглушки и обычная пишут в один стейт — общие ветки, чтобы они не разошлись.
    builder.addMatcher(isAnyOf(authCheck.pending, authCheckSilent.pending), (state) => {
      state.authChecking = true;
    });

    builder.addMatcher(isAnyOf(authCheck.rejected, authCheckSilent.rejected), (state) => {
      rejectAuthCheck(state);
    });
  },
});

function applyAuthCheckResult(state: AuthSliceState, payload: AuthCheckResponse | null): void {
  state.hasTournaments = Boolean(payload?.tournament);

  if (!payload?.tournament) {
    state.activeTournamentId = null;
  } else if (!state.activeTournamentId) {
    state.activeTournamentId = payload.tournament.id;
  }

  state.authed = true;
  state.authChecking = false;
  state.authChecked = true;
  state.user = payload?.user || null;
}

function rejectAuthCheck(state: AuthSliceState): void {
  state.authed = false;
  state.authChecking = false;
  state.authChecked = true;
  state.user = null;
}

export const { resetAuthChecking, setTournamentCreated } = slice.actions;

const authReducer = slice.reducer;
export default authReducer;
